import LinearProgress from "@mui/material/LinearProgress";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type HorizontalProgressPlacement = "navbar" | "inline";

interface HorizontalProgressProps {
  /**
   * - "navbar" (default): absolutely positioned to sit just below its
   *   `position: relative` parent, no layout shift. Used by the global Navbar.
   * - "inline": rendered in normal flow at its insertion point. Use for
   *   feature pages that want a local loading indicator (e.g. list views).
   */
  placement?: HorizontalProgressPlacement;
}

export function HorizontalProgress({
  placement = "navbar",
}: HorizontalProgressProps = {}) {
  const isLoading = useRouterState({
    select: (s) => s.isLoading,
  });

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    let timeoutId: number | undefined;

    if (isLoading) {
      timeoutId = window.setTimeout(() => {
        setShowLoader(true);
      }, 350);
    } else {
      setShowLoader(false);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  if (!showLoader) return null;

  if (placement === "inline") {
    return <LinearProgress sx={{ borderRadius: 0 }} />;
  }

  return (
    <LinearProgress
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        transform: "translateY(100%)",
        borderRadius: 0,
      }}
    />
  );
}
